import { useContext, useEffect, useState } from 'react';
import AuthContext from '../context/authContext';
import { useHTTP } from './useHttp';

// Looks up the bookings belonging to a phone number, so a call protocol can show who is calling
// without the employee switching views. Uses the existing internal-booking search, whose
// `searchQuery` covers the `phone` field.
//
// Silent by design: a caller with no bookings is the normal case, and a user without
// `internalBooking:read` simply does not get the panel — neither is worth an error toast while a
// phone is ringing.
export const useCallerBookings = (caller, limit = 5) => {
  const authCtx = useContext(AuthContext);
  const { sendRequest } = useHTTP();
  const [bookings, setBookings] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const canSearch = !!authCtx.checkRight?.('internalBooking:read');

  useEffect(() => {
    const normalized = String(caller || '').trim();
    // The backend ignores anything shorter than three characters, and 'anonymous' is what
    // Comdesk sends for a withheld number — neither is worth a request.
    if (!canSearch || normalized.length < 3 || normalized.toLowerCase() === 'anonymous') {
      setBookings([]);
      return undefined;
    }

    let cancelled = false;
    setIsLoading(true);

    sendRequest({
      url: '/api/v1/internalBooking',
      method: 'GET',
      headers: authCtx.getAuthHeaders(),
      params: { searchQuery: normalized, limit, page: 1, sortKey: 'startTime', sortDirection: 'desc' },
    })
      .then(res => {
        if (cancelled) return;
        const rows = Array.isArray(res) ? res : res?.internalBookings || res?.data || [];
        setBookings(rows.slice(0, limit));
      })
      .catch(() => {
        if (!cancelled) setBookings([]);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [caller, canSearch, limit, authCtx, sendRequest]);

  return { bookings, isLoading, canSearch };
};

export default useCallerBookings;
